import type { Dayjs } from "dayjs";
import dayjs, { isDayjs } from "dayjs";
import uniq from "lodash/uniq";

import Sentry from "@similarweb/sentry-logger";

import { swSettings } from "app/common/services/swSettings";
import { CAMPAIGNS_ALL_TIME_ID } from "app/components/campaigns/queryBar-report/reports-pages-filters/durationPicker/CampaignDurationPicker.consts";
import { getAllTimeDateRangeForCampaign } from "app/components/campaigns/queryBar-report/reports-pages-filters/durationPicker/CampaignDurationPickerUtils";
import { RANK_TRACKER_CAMPAIGNS_CONFIG_KEY } from "app/common/campaigns/KeywordsCampaignConsts";
import { fetchCampaigns } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsInfraUtils";
import type {
    IKeywordsCampaignData,
    IKeywordsCampaignsCampaignRawData,
    IRankTrackerCompetitor,
    RtCampaignScrapeContext,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { sitesColors } from "app/common/campaigns/KeywordsCampaginKeywordsTableConsts";
import type { DatesRange } from "app/pages/digital-marketing/keywords-campaigns/shared/types";
import DurationService from "app/services/DurationService";
import { fetchWebsitesFaviconsPostQuery } from "app/services/sitesService/queries/sitesQueries";
import type { IWebsitesFavicons } from "app/services/sitesService/types";

interface ICampaignConfigParamsForEnrichment {
    site: string;
    name?: string;
    competitors?: string[] | IRankTrackerCompetitor[];
    createdTime: Dayjs;
}
interface ICampaignConfigEnriched {
    asset: string;
    color: string;
    site: string;
    createdTime: Dayjs;
    competitors: IRankTrackerCompetitor[];
}

const fetchIconsRequests = async (domains: string[]): Promise<IWebsitesFavicons> => {
    try {
        return await fetchWebsitesFaviconsPostQuery(domains);
    } catch (error) {
        Sentry.error(`Error fetching icons for reports`, error);
        return {};
    }
};

const getCampaignAsset = async <T extends ICampaignConfigParamsForEnrichment>(
    campaignConfigs: T[],
): Promise<IWebsitesFavicons> => {
    const sitesList = campaignConfigs?.map((campaign) => {
        return campaign.competitors ? [campaign.site, ...campaign.competitors] : [campaign.site];
    });
    const flatList = sitesList ? sitesList.flat() : [];
    const uniqSites = uniq(flatList) as string[];
    return await fetchIconsRequests(uniqSites);
};

/**
 * Latest calendar day available for a Rank Tracker campaign (ISO date string).
 *
 * Uses `lastScrapeDate` when set; otherwise falls back to `LastAvailableRankTrackerEtlDate`.
 */
export const getCampaignMaxAvailableDate = (lastScrapeDate?: string | null): string => {
    const etlDate = swSettings.components.RankTracker?.resources?.LastAvailableRankTrackerEtlDate as string;
    return lastScrapeDate ?? etlDate;
};

/** Parses a Dayjs instance or ISO string to a valid UTC Dayjs, or undefined if missing/invalid. */
export const toDayjsUtc = (value: Dayjs | string | undefined): Dayjs | undefined => {
    if (!value) {
        return undefined;
    }
    const parsed = isDayjs(value) ? value : dayjs.utc(value);
    return parsed.isValid() ? parsed : undefined;
};

export const withCampaignDatesUtc = <T extends { createdTime?: Dayjs | string; updatedTime?: Dayjs | string }>(
    campaign: T,
): T => {
    const createdTime = toDayjsUtc(campaign?.createdTime);
    const updatedTime = toDayjsUtc(campaign?.updatedTime);
    if (!createdTime && !updatedTime) {
        return campaign;
    }
    return {
        ...campaign,
        ...(createdTime && { createdTime }),
        ...(updatedTime && { updatedTime }),
    };
};

export type RtCampaignDurationBounds = {
    dataStart: Dayjs;
    maxDate: string;
};

/**
 * First UTC calendar day with rank data for a campaign.
 * Defined only when `firstScrapeTime` is set (scrape has completed).
 */
export const getRtCampaignDataStartDate = (campaign: RtCampaignScrapeContext | null | undefined): Dayjs | undefined => {
    if (!campaign?.firstScrapeTime) {
        return undefined;
    }
    const parsed = dayjs.utc(campaign.firstScrapeTime).startOf("day");
    return parsed.isValid() ? parsed : undefined;
};

/**
 * Campaign data start and max available day for duration UI and all-time checks.
 *
 * Returns `null` when `firstScrapeTime` is absent (campaign not scraped yet). Downstream behavior:
 * - Navigation default duration → `"0d"` ({@link getDurationForNavigation})
 * - Dashboard/widget all-time URL → {@link FIRST_DAY_CREATION_ID} ({@link getDurationForURLWithCampaignMax})
 * - Report API all-time range → single max/ETL day ({@link getRTDurationObjectForCampaign})
 * - `isAllTimeSelected` → `false` (no bounds to compare against)
 */
export const getRtCampaignDurationBounds = (
    campaign: RtCampaignScrapeContext | null | undefined,
): RtCampaignDurationBounds | null => {
    const dataStart = getRtCampaignDataStartDate(campaign);
    if (!dataStart || !campaign) {
        return null;
    }
    return {
        dataStart,
        maxDate: getCampaignMaxAvailableDate(campaign.lastScrapeDate),
    };
};

/**
 * Default URL duration when entering RT reports or selecting a campaign in the dashboard wizard.
 * Uses {@link getRtCampaignDataStartDate} (first scrape day), not `createdTime`.
 */
export const getDefaultDurationForRankTrackerCampaign = (
    campaign: RtCampaignScrapeContext | null | undefined,
): string => {
    const maxDate = getCampaignMaxAvailableDate(campaign?.lastScrapeDate);
    const maxDayjs = dayjs.utc(maxDate);
    const monthAgo = maxDayjs.subtract(1, "month");
    const rangeStart = getRtCampaignDataStartDate(campaign);
    if (!rangeStart) {
        return "0d";
    }
    const amountOfDays = maxDayjs.diff(rangeStart, "days");
    return rangeStart.isBefore(monthAgo) ? "1m" : `${Math.max(amountOfDays, 0)}d`;
};

/** Ensures a resolved range does not end after the campaign max (e.g. stale custom URL ranges). */
const clampDurationRangeToMaxDate = (range: DatesRange, maxDate: string): DatesRange => {
    const maxDayjs = dayjs.utc(maxDate).endOf("day");
    const to = range.to.isAfter(maxDayjs) ? maxDayjs : range.to;
    const from = range.from.isAfter(to) ? to : range.from;
    return { from, to };
};

/**
 * Resolves the route `duration` param to `{ from, to }` for report API calls.
 *
 * Presets (e.g. `1m`) and custom ranges (`YYYY.MM.DD-YYYY.MM.DD`) use the campaign max
 * from {@link getCampaignMaxAvailableDate}. Prefer this over `DurationService.getDurationObjectForCampaigns` in RT reports.
 *
 * @param duration - URL duration: preset id, all-time id, or custom range string
 * @param campaign - Current campaign (needs `lastScrapeDate`; data start for all-time)
 */
export const getRTDurationObjectForCampaign = (
    duration: string,
    campaign: RtCampaignScrapeContext | null | undefined,
): DatesRange => {
    const maxDate = getCampaignMaxAvailableDate(campaign?.lastScrapeDate);
    const dataStart = getRtCampaignDataStartDate(campaign);
    const maxDayStart = dayjs.utc(maxDate).startOf("day");
    const maxDayEnd = dayjs.utc(maxDate).endOf("day");
    // "all" isn't a preset — DurationService would give a bogus 1-day range. Use campaign start → max day instead.
    const range =
        duration === CAMPAIGNS_ALL_TIME_ID
            ? dataStart
                ? getAllTimeDateRangeForCampaign(dataStart, maxDate)
                : { from: maxDayStart, to: maxDayEnd }
            : DurationService.getDurationObjectForCampaigns(duration, maxDate);
    return clampDurationRangeToMaxDate(range, maxDate);
};

/**
 * Min/max bounds for the Rank Tracker date picker (`durationObject` in RT context).
 *
 * `from` is {@link getRtCampaignDataStartDate} (start of UTC day); `to` is the campaign max (end of UTC day).
 * Recomputed in {@link RTContextReducer} when the campaign loads or updates.
 */
export const getAvailableDurationObjectForRTContext = (
    currentCampaignConfig: RtCampaignScrapeContext | null | undefined,
): DatesRange => {
    const maxDate = getCampaignMaxAvailableDate(currentCampaignConfig?.lastScrapeDate);
    // Align dates to full UTC-day boundaries (startOf/endOf)
    // to ensure consistent behavior in the date picker across time zones
    const maxCampaignDate: Dayjs = (maxDate ? dayjs.utc(maxDate) : dayjs().utc()).endOf("day");
    const dataStart = getRtCampaignDataStartDate(currentCampaignConfig);
    const minCampaignDate: Dayjs = dataStart ?? maxCampaignDate.startOf("day");
    return { from: minCampaignDate, to: maxCampaignDate };
};

const getCampaignsWithIconsWrapper = <T extends ICampaignConfigParamsForEnrichment>(
    listOfFavicons: IWebsitesFavicons,
    campaignConfigs: T[],
): (T & ICampaignConfigEnriched)[] => {
    const managedCampaigns = campaignConfigs?.map((config: T) => {
        const asset = listOfFavicons[config.site];
        return {
            ...config,
            asset,
            color: sitesColors[0], //main site should have the same color always
            competitors: config.competitors?.map((competitor, index) => {
                return {
                    site: competitor,
                    asset: listOfFavicons[competitor],
                    color: sitesColors[index + 1], //as the main site already took the first color
                };
            }),
        };
    });
    return managedCampaigns;
};

export const enrichCampaignsWithIcons = async <T extends ICampaignConfigParamsForEnrichment>(
    campaignConfigs: T[],
): Promise<(T & ICampaignConfigEnriched)[]> => {
    try {
        const listOfFavicons: IWebsitesFavicons = await getCampaignAsset(campaignConfigs);
        return getCampaignsWithIconsWrapper(listOfFavicons, campaignConfigs);
    } catch (error) {
        Sentry.error(`Error fetching icons for campaigns configurations from server -`, error);
        return getCampaignsWithIconsWrapper({}, campaignConfigs);
    }
};

export const getCampaignsFromServer = async (): Promise<IKeywordsCampaignData[]> => {
    const campaignConfigs: IKeywordsCampaignsCampaignRawData[] = await fetchCampaigns();
    const campaignsWithIcons: IKeywordsCampaignData[] = await enrichCampaignsWithIcons(
        campaignConfigs as IKeywordsCampaignData[],
    );
    const campaignsAsString = JSON.stringify(campaignsWithIcons);
    localStorage.setItem(RANK_TRACKER_CAMPAIGNS_CONFIG_KEY, campaignsAsString);
    return campaignsWithIcons;
};
